(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const desktop = matchMedia('(min-width: 64rem)');
  const revealSelector = '[data-motion-reveal],.info-section,.info-card,.info-contact__form,.info-contact__support,.info-faq__item';
  const realtimeSelector = '.hero-banner__media,.media-content__media,.collection-tile__media,.editorial-card__media,.product-card__media';
  let revealObserver = null;

  const loadMotionStyles = () => {
    const current = document.currentScript?.src || import.meta.url;
    const ensure = (filename, marker) => {
      if (document.querySelector(`link[${marker}]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = new URL(filename, current).href;
      link.setAttribute(marker, 'true');
      document.head.appendChild(link);
    };
    ensure('motion-hero-micro.css', 'data-muaazon-hero-micro');
    ensure('motion-marketplace-hero.css', 'data-muaazon-marketplace-hero');
    ensure('motion-commerce.css', 'data-muaazon-commerce-motion');
  };

  const all = (root, selector) => {
    const nodes = [...(root?.querySelectorAll?.(selector) ?? [])];
    if (root instanceof Element && root.matches(selector)) nodes.unshift(root);
    return nodes;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const realtimeEnabled = () => !reduced.matches && finePointer.matches && desktop.matches;

  const prepareReveals = (root = document) => {
    all(root, revealSelector).forEach((element, index) => {
      if (element.dataset.motionBound) return;
      element.dataset.motionBound = 'true';
      if (!element.hasAttribute('data-motion-reveal')) element.setAttribute('data-motion-reveal', '');
      element.style.setProperty('--motion-index', String(index % 8));
      element.classList.add('motion-ready');
      if (reduced.matches) element.classList.add('is-motion-visible');
      else revealObserver?.observe(element);
    });
  };

  const prepareStagger = (root = document) => {
    root.querySelectorAll?.('.product-grid,.collection-product-grid,[data-product-grid],.info-card-grid,.info-faq__list').forEach((element) => {
      element.setAttribute('data-motion-stagger', '');
    });
  };

  const resetRealtime = (element) => {
    element.style.setProperty('--rt-x', '0px');
    element.style.setProperty('--rt-y', '0px');
    element.style.setProperty('--rt-rx', '0deg');
    element.style.setProperty('--rt-ry', '0deg');
    element.style.setProperty('--rt-s', '1');
  };

  const prepareRealtime = (root = document) => {
    if (!realtimeEnabled()) return;
    all(root, realtimeSelector).forEach((element) => {
      if (element.dataset.rtBound) return;
      element.dataset.rtBound = 'true';
      element.classList.add('motion-realtime', 'is-rt-active');
      let raf = 0;
      let pending = null;
      let rect = null;

      const refreshRect = () => {
        if (!element.isConnected || !realtimeEnabled()) { rect = null; return; }
        const next = element.getBoundingClientRect();
        rect = next.width && next.height ? next : null;
      };

      const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(refreshRect) : null;
      resizeObserver?.observe(element);

      element.addEventListener('pointerenter', refreshRect, { passive: true });
      element.addEventListener('pointermove', (event) => {
        if (!realtimeEnabled() || !rect) return;
        pending = { clientX: event.clientX, clientY: event.clientY };
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const current = pending;
          pending = null;
          const cached = rect;
          if (!current || !cached?.width || !cached?.height) return;
          const x = clamp(((current.clientX - cached.left) / cached.width - 0.5) * 2, -1, 1);
          const y = clamp(((current.clientY - cached.top) / cached.height - 0.5) * 2, -1, 1);
          element.style.setProperty('--rt-x', `${(x * 1.1).toFixed(2)}px`);
          element.style.setProperty('--rt-y', `${(y * 0.65).toFixed(2)}px`);
          element.style.setProperty('--rt-rx', `${(-y * 1.1).toFixed(3)}deg`);
          element.style.setProperty('--rt-ry', `${(x * 1.1).toFixed(3)}deg`);
          element.style.setProperty('--rt-s', '1.004');
        });
      }, { passive: true });

      element.addEventListener('pointerleave', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        pending = null;
        rect = null;
        resetRealtime(element);
      }, { passive: true });
    });
  };

  const init = (root = document) => {
    prepareStagger(root);
    prepareReveals(root);
    prepareRealtime(root);
  };

  const boot = () => {
    loadMotionStyles();
    if (!reduced.matches) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-motion-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    init();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('pointerdown', (event) => {
    const control = event.target.closest?.('.button,button,[role="button"],[data-swatch],.swatch');
    if (control && !reduced.matches) control.classList.add('is-motion-pressed');
  }, { passive: true });
  for (const type of ['pointerup', 'pointercancel']) {
    document.addEventListener(type, () => document.querySelectorAll('.is-motion-pressed').forEach((element) => element.classList.remove('is-motion-pressed')), { passive: true });
  }

  reduced.addEventListener?.('change', () => location.reload());
  finePointer.addEventListener?.('change', () => { if (realtimeEnabled()) prepareRealtime(); });
  desktop.addEventListener?.('change', () => { if (realtimeEnabled()) prepareRealtime(); });
})();
